import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { StoneType } from "@/types/stone-type/stone-type.types";
import { InformationSource } from "@/types/information-source/information-source.types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { stoneTypeService } from "@/services/stone-type/stoneTypeService";
import { useData } from "@/hooks/useData";

interface StoneTypeFormProps {
  isOpen: boolean;
  onClose: () => void;
  stoneTypeToEdit?: StoneType | null;
  onSuccess: () => void;
}

export const StoneTypeForm = ({
  isOpen,
  onClose,
  stoneTypeToEdit,
  onSuccess,
}: StoneTypeFormProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Form state
  const [name, setName] = useState("");

  // Density
  const [densityValue, setDensityValue] = useState<number | undefined>(
    undefined
  );
  const [densitySourceRef, setDensitySourceRef] = useState<string>("");

  // Emission factor for extraction
  const [feExtracaoValue, setFeExtracaoValue] = useState<number | undefined>(
    undefined
  );
  const [feExtracaoSourceRef, setFeExtracaoSourceRef] = useState<string>("");

  // Water factor for extraction
  const [fhExtracaoValue, setFhExtracaoValue] = useState<number | undefined>(
    undefined
  );
  const [fhExtracaoSourceRef, setFhExtracaoSourceRef] = useState<string>("");

  // Validation state
  const [nameError, setNameError] = useState("");
  const [densityValueError, setDensityValueError] = useState("");
  const [feExtracaoValueError, setFeExtracaoValueError] = useState("");
  const [fhExtracaoValueError, setFhExtracaoValueError] = useState("");

  // Fetch information sources for the dropdown
  const { data: infoSources, loading: loadingInfoSources } =
    useData<InformationSource>({
      collectionName: "setup_infoSources",
    });

  // Initialize form when editing
  useEffect(() => {
    if (stoneTypeToEdit) {
      setName(stoneTypeToEdit.name || "");

      // Set density values if they exist
      setDensityValue(stoneTypeToEdit.dens_pedra?.value);
      setDensitySourceRef(stoneTypeToEdit.dens_pedra?.sourceId || "");

      // Set emission factor values if they exist
      setFeExtracaoValue(stoneTypeToEdit.fe_extracao?.value);
      setFeExtracaoSourceRef(stoneTypeToEdit.fe_extracao?.sourceId || "");

      // Set water factor values if they exist
      setFhExtracaoValue(stoneTypeToEdit.fh_extracao?.value);
      setFhExtracaoSourceRef(stoneTypeToEdit.fh_extracao?.sourceId || "");
    } else {
      // Reset form for new creation
      setName("");

      setDensityValue(undefined);
      setDensitySourceRef("");

      setFeExtracaoValue(undefined);
      setFeExtracaoSourceRef("");

      setFhExtracaoValue(undefined);
      setFhExtracaoSourceRef("");
    }

    // Reset validation errors
    setNameError("");
    setDensityValueError("");
    setFeExtracaoValueError("");
    setFhExtracaoValueError("");
  }, [stoneTypeToEdit, isOpen]);

  const validateForm = () => {
    let isValid = true;

    // Validate name
    if (!name.trim()) {
      setNameError(t("validation.required"));
      isValid = false;
    } else {
      setNameError("");
    }

    // Validate density if provided
    if (densityValue !== undefined) {
      if (isNaN(densityValue) || densityValue <= 0) {
        setDensityValueError(t("stoneTypes.validation.invalidValue"));
        isValid = false;
      } else if (!densitySourceRef) {
        setDensityValueError(t("stoneTypes.validation.sourceRequired"));
        isValid = false;
      } else {
        setDensityValueError("");
      }
    }

    // Validate emission factor if provided
    if (feExtracaoValue !== undefined) {
      if (isNaN(feExtracaoValue) || feExtracaoValue < 0) {
        setFeExtracaoValueError(t("stoneTypes.validation.invalidValue"));
        isValid = false;
      } else if (!feExtracaoSourceRef) {
        setFeExtracaoValueError(t("stoneTypes.validation.sourceRequired"));
        isValid = false;
      } else {
        setFeExtracaoValueError("");
      }
    }

    // Validate water factor if provided
    if (fhExtracaoValue !== undefined) {
      if (isNaN(fhExtracaoValue) || fhExtracaoValue < 0) {
        setFhExtracaoValueError(t("stoneTypes.validation.invalidValue"));
        isValid = false;
      } else if (!fhExtracaoSourceRef) {
        setFhExtracaoValueError(t("stoneTypes.validation.sourceRequired"));
        isValid = false;
      } else {
        setFhExtracaoValueError("");
      }
    }

    return isValid;
  };

  const handleNumberInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<number | undefined>>
  ) => {
    const value = e.target.value;
    if (value === "") {
      setter(undefined);
      return;
    }

    if (value === "." || value === "-" || value === "-.") {
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setter(numValue);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const stoneTypeData: Partial<StoneType> = {
        name: name.trim(),
      };

      // Add density if both value and source are provided
      if (densityValue !== undefined && densitySourceRef) {
        stoneTypeData.dens_pedra = {
          value: densityValue,
          sourceId: densitySourceRef,
        };
      }

      // Add emission factor if both value and source are provided
      if (feExtracaoValue !== undefined && feExtracaoSourceRef) {
        stoneTypeData.fe_extracao = {
          value: feExtracaoValue,
          sourceId: feExtracaoSourceRef,
        };
      }

      // Add water factor if both value and source are provided
      if (fhExtracaoValue !== undefined && fhExtracaoSourceRef) {
        stoneTypeData.fh_extracao = {
          value: fhExtracaoValue,
          sourceId: fhExtracaoSourceRef,
        };
      }

      if (stoneTypeToEdit) {
        // Update existing stone type
        await stoneTypeService.edit(
          stoneTypeToEdit.id,
          stoneTypeData,
          user?.uid
        );
        toast({
          title: t("success"),
          description: t("stoneTypes.updated"),
        });
      } else {
        // Create new stone type
        await stoneTypeService.create(
          stoneTypeData as Omit<StoneType, "id" | "createdAt" | "updatedAt">,
          user?.uid
        );
        toast({
          title: t("success"),
          description: t("stoneTypes.created"),
        });
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving stone type:", error);
      toast({
        title: t("error"),
        description: stoneTypeToEdit
          ? t("stoneTypes.updateError")
          : t("stoneTypes.createError"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Filter out archived info sources
  const activeInfoSources =
    infoSources?.filter((source) => !source.isArchived) || [];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>
            {stoneTypeToEdit
              ? t("stoneTypes.editTitle")
              : t("stoneTypes.addTitle")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-5 py-4">
            {/* Name field */}
            <div className="grid gap-2">
              <Label htmlFor="name" className="flex items-center gap-1">
                {t("stoneTypes.name")}
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={nameError ? "border-red-500" : ""}
                disabled={isLoading}
              />
              {nameError && <p className="text-red-500 text-sm">{nameError}</p>}
            </div>

            {/* Density section */}
            <fieldset className="space-y-3 pt-3 border-t border-gray-200">
              <legend className="font-medium text-sm px-2 -ml-2">
                {t("stoneTypes.density.title")}
              </legend>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="densityValue">
                    {t("stoneTypes.density.value")}
                  </Label>
                  <Input
                    id="densityValue"
                    type="number"
                    step="any"
                    min="0"
                    placeholder={t("stoneTypes.density.valuePlaceholder")}
                    value={densityValue === undefined ? "" : densityValue}
                    onChange={(e) =>
                      handleNumberInputChange(e, setDensityValue)
                    }
                    className={densityValueError ? "border-red-500" : ""}
                    disabled={isLoading}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="densitySource">
                    {t("stoneTypes.density.source")}
                  </Label>
                  <Select
                    value={densitySourceRef}
                    onValueChange={setDensitySourceRef}
                    disabled={
                      isLoading ||
                      loadingInfoSources ||
                      densityValue === undefined
                    }
                  >
                    <SelectTrigger id="densitySource" className="w-full">
                      <SelectValue
                        placeholder={t("stoneTypes.density.sourcePlaceholder")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {activeInfoSources.map((source) => (
                        <SelectItem key={source.id} value={source.id}>
                          {source.name} ({source.year})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {densityValueError && (
                <p className="text-red-500 text-sm col-span-2">
                  {densityValueError}
                </p>
              )}
            </fieldset>

            {/* Emission factor section */}
            <fieldset className="space-y-3 pt-3 border-t border-gray-200">
              <legend className="font-medium text-sm px-2 -ml-2">
                {t("stoneTypes.fe_extracao.title")}
              </legend>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="feExtracaoValue">
                    {t("stoneTypes.fe_extracao.value")}
                  </Label>
                  <Input
                    id="feExtracaoValue"
                    type="number"
                    step="any"
                    min="0"
                    placeholder={t("stoneTypes.fe_extracao.valuePlaceholder")}
                    value={feExtracaoValue === undefined ? "" : feExtracaoValue}
                    onChange={(e) =>
                      handleNumberInputChange(e, setFeExtracaoValue)
                    }
                    className={feExtracaoValueError ? "border-red-500" : ""}
                    disabled={isLoading}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="feExtracaoSource">
                    {t("stoneTypes.fe_extracao.source")}
                  </Label>
                  <Select
                    value={feExtracaoSourceRef}
                    onValueChange={setFeExtracaoSourceRef}
                    disabled={
                      isLoading ||
                      loadingInfoSources ||
                      feExtracaoValue === undefined
                    }
                  >
                    <SelectTrigger id="feExtracaoSource" className="w-full">
                      <SelectValue
                        placeholder={t(
                          "stoneTypes.fe_extracao.sourcePlaceholder"
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {activeInfoSources.map((source) => (
                        <SelectItem key={source.id} value={source.id}>
                          {source.name} ({source.year})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {feExtracaoValueError && (
                <p className="text-red-500 text-sm">{feExtracaoValueError}</p>
              )}
            </fieldset>

            {/* Water factor section */}
            <fieldset className="space-y-3 pt-3 border-t border-gray-200">
              <legend className="font-medium text-sm px-2 -ml-2">
                {t("stoneTypes.fh_extracao.title")}
              </legend>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="fhExtracaoValue">
                    {t("stoneTypes.fh_extracao.value")}
                  </Label>
                  <Input
                    id="fhExtracaoValue"
                    type="number"
                    step="any"
                    min="0"
                    placeholder={t("stoneTypes.fh_extracao.valuePlaceholder")}
                    value={fhExtracaoValue === undefined ? "" : fhExtracaoValue}
                    onChange={(e) =>
                      handleNumberInputChange(e, setFhExtracaoValue)
                    }
                    className={fhExtracaoValueError ? "border-red-500" : ""}
                    disabled={isLoading}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="fhExtracaoSource">
                    {t("stoneTypes.fh_extracao.source")}
                  </Label>
                  <Select
                    value={fhExtracaoSourceRef}
                    onValueChange={setFhExtracaoSourceRef}
                    disabled={
                      isLoading ||
                      loadingInfoSources ||
                      fhExtracaoValue === undefined
                    }
                  >
                    <SelectTrigger id="fhExtracaoSource" className="w-full">
                      <SelectValue
                        placeholder={t(
                          "stoneTypes.fh_extracao.sourcePlaceholder"
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {activeInfoSources.map((source) => (
                        <SelectItem key={source.id} value={source.id}>
                          {source.name} ({source.year})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {fhExtracaoValueError && (
                <p className="text-red-500 text-sm">{fhExtracaoValueError}</p>
              )}
            </fieldset>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-gray-300 hover:bg-gray-100"
              disabled={isLoading}
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {isLoading ? t("saving") : t("save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
